// =============================================================================
// Consolidated Price Service
// Single source of truth for all token price data.
// Replaces: priceService (original), predictionMonitorService.getTokenPrice,
//   simpleXPChecker DexScreener fetch, xpService._checkIfWon inline fetch,
//   xpService.getSOLPrice inline fetch
// =============================================================================

import logger from '../utils/logger';

class PriceService {
  constructor() {
    this.baseUrls = {
      coingecko: 'https://api.coingecko.com/api/v3',
      dexscreener: 'https://api.dexscreener.com/latest/dex'
    };

    // Cache for price data
    this.priceCache = new Map();
    this.cacheExpiry = 60000; // 60 seconds (increased to reduce API calls)

    // SOL price cache (separate, 60s TTL)
    this.solPriceCache = null;
    this.solPriceCacheTime = 0;

    // Rate limiting tracking
    this.lastApiCall = {
      coingecko: 0,
      dexscreener: 0
    };
    this.minApiDelay = {
      coingecko: 10000, // 10 seconds between CoinGecko calls
      dexscreener: 1000  // 1 second between DexScreener calls
    };
  }

  // Get token price from multiple sources
  async getTokenPrice(tokenAddress, tokenSymbol) {
    try {
      // Validate inputs
      if (!tokenAddress && !tokenSymbol) {
        throw new Error('Either tokenAddress or tokenSymbol is required');
      }

      // Check cache first
      const cacheKey = `${tokenAddress || 'none'}_${tokenSymbol || 'none'}`;
      const cached = this.priceCache.get(cacheKey);
      
      if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
        logger.log(`💾 Using cached price for ${tokenSymbol || tokenAddress}`);
        return cached.data;
      }

      logger.log(`🔍 Fetching fresh price data for ${tokenSymbol || tokenAddress}`);
      
      // Try multiple APIs for best accuracy
      const priceData = await this.fetchFromMultipleSources(tokenAddress, tokenSymbol);
      
      if (!priceData) {
        logger.warn(`⚠️ No price data found for ${tokenSymbol || tokenAddress}`);
        return null;
      }
      
      // Cache the result
      this.priceCache.set(cacheKey, {
        data: priceData,
        timestamp: Date.now()
      });

      logger.log(`✅ Price data fetched successfully: $${priceData.price}`);
      return priceData;
    } catch (error) {
      // Only log actual errors, not "no data found" warnings
      if (error.message !== 'No valid price data found') {
        logger.error('Error fetching token price:', error);
      }
      return null;
    }
  }

  // Fetch from multiple price sources
  async fetchFromMultipleSources(tokenAddress, tokenSymbol) {
    const results = await Promise.allSettled([
      this.fetchFromDexScreener(tokenAddress),
      this.fetchFromCoinGecko(tokenSymbol)
    ]);

    // Find the best result
    const validResults = results
      .filter(result => result.status === 'fulfilled' && result.value)
      .map(result => result.value);

    if (validResults.length === 0) {
      logger.warn('⚠️ No price data found from external APIs');
      return null;
    }

    // Return the most recent/accurate price
    return validResults[0];
  }

  // DexScreener API (good for Solana tokens)
  async fetchFromDexScreener(tokenAddress) {
    try {
      const response = await fetch(`${this.baseUrls.dexscreener}/tokens/${tokenAddress}`);
      const data = await response.json();
      
      if (data.pairs && data.pairs.length > 0) {
        const pair = data.pairs[0]; // Get the most liquid pair
        return {
          price: parseFloat(pair.priceUsd),
          volume24h: parseFloat(pair.volume?.h24 || 0),
          priceChange24h: parseFloat(pair.priceChange?.h24 || 0),
          marketCap: parseFloat(pair.marketCap || 0),
          liquidity: parseFloat(pair.liquidity?.usd || 0),
          fdv: parseFloat(pair.fdv || 0),
          source: 'dexscreener',
          timestamp: Date.now(),
          pairAddress: pair.pairAddress,
          dexId: pair.dexId,
          // Include metadata from baseToken
          name: pair.baseToken?.name || null,
          symbol: pair.baseToken?.symbol || null,
          image: pair.info?.imageUrl || null
        };
      }
    } catch (error) {
      console.debug('DexScreener API unavailable for token');
      return null;
    }
  }

  // CoinGecko API (good for major tokens)
  async fetchFromCoinGecko(tokenSymbol) {
    try {
      // Check rate limiting
      const now = Date.now();
      const timeSinceLastCall = now - this.lastApiCall.coingecko;
      if (timeSinceLastCall < this.minApiDelay.coingecko) {
        logger.log(`⏳ Rate limiting CoinGecko - waiting ${this.minApiDelay.coingecko - timeSinceLastCall}ms`);
        return null;
      }
      this.lastApiCall.coingecko = now;
      
      // First, search for the token ID
      const searchResponse = await fetch(
        `${this.baseUrls.coingecko}/search?query=${tokenSymbol}`
      );
      
      // Check for rate limiting response
      if (searchResponse.status === 429) {
        logger.warn('⚠️ CoinGecko rate limit hit, using cache or fallback');
        return null;
      }
      
      const searchData = await searchResponse.json();
      
      if (searchData.coins && searchData.coins.length > 0) {
        const tokenId = searchData.coins[0].id;
        
        // Get price data
        const priceResponse = await fetch(
          `${this.baseUrls.coingecko}/simple/price?ids=${tokenId}&vs_currencies=usd&include_24hr_change=true&include_market_cap=true&include_24hr_vol=true`
        );
        
        // Check for rate limiting response
        if (priceResponse.status === 429) {
          logger.warn('⚠️ CoinGecko rate limit hit on price fetch');
          return null;
        }
        
        const priceData = await priceResponse.json();
        
        if (priceData[tokenId]) {
          const data = priceData[tokenId];
          return {
            price: data.usd,
            volume24h: data.usd_24h_vol,
            priceChange24h: data.usd_24h_change,
            marketCap: data.usd_market_cap,
            source: 'coingecko',
            timestamp: Date.now(),
            tokenId: tokenId
          };
        }
      }
    } catch (error) {
      console.debug('CoinGecko API unavailable for token');
      return null;
    }
  }

  // ===========================================================================
  // SIMPLE PRICE ACCESSORS (consolidated from predictionMonitorService,
  // simpleXPChecker, xpService._checkIfWon, xpService.getSOLPrice)
  // ===========================================================================

  // Get just the USD price number for a token (DexScreener only, cached 5min)
  // Replaces: predictionMonitorService.getTokenPrice, simpleXPChecker inline fetch
  async getDexScreenerPrice(tokenAddress) {
    const cacheKey = `dex_simple_${tokenAddress}`;
    const cached = this.priceCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < 300000) { // 5 min cache
      return cached.data;
    }

    try {
      const response = await fetch(`${this.baseUrls.dexscreener}/tokens/${tokenAddress}`);
      if (!response.ok) return null;

      const data = await response.json();
      if (!data.pairs || data.pairs.length === 0) return null;

      // Pick the pair with highest liquidity
      const bestPair = data.pairs.reduce((best, pair) =>
        (pair.liquidity?.usd || 0) > (best.liquidity?.usd || 0) ? pair : best
      );

      const price = parseFloat(bestPair.priceUsd || 0);
      this.priceCache.set(cacheKey, { data: price, timestamp: Date.now() });
      return price;
    } catch (error) {
      console.debug('DexScreener simple price fetch failed:', error.message);
      return null;
    }
  }

  // Get full DexScreener pair data (price + marketCap + fdv etc.)
  // Replaces: xpService._checkIfWon inline fetch, simpleXPChecker full data fetch
  async getDexScreenerFullData(tokenAddress) {
    const cacheKey = `dex_full_${tokenAddress}`;
    const cached = this.priceCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < 300000) {
      return cached.data;
    }

    try {
      const response = await fetch(`${this.baseUrls.dexscreener}/tokens/${tokenAddress}`);
      if (!response.ok) return null;

      const data = await response.json();
      if (!data.pairs || data.pairs.length === 0) return null;

      const pair = data.pairs[0];
      const result = {
        price: parseFloat(pair.priceUsd || 0),
        marketCap: parseFloat(pair.marketCap || pair.fdv || 0),
        fdv: parseFloat(pair.fdv || 0),
        liquidity: parseFloat(pair.liquidity?.usd || 0),
        volume24h: parseFloat(pair.volume?.h24 || 0),
        priceChange24h: parseFloat(pair.priceChange?.h24 || 0),
        pairAddress: pair.pairAddress,
        dexId: pair.dexId,
        name: pair.baseToken?.name || null,
        symbol: pair.baseToken?.symbol || null,
        image: pair.info?.imageUrl || null,
        source: 'dexscreener',
        timestamp: Date.now()
      };

      this.priceCache.set(cacheKey, { data: result, timestamp: Date.now() });
      return result;
    } catch (error) {
      console.debug('DexScreener full data fetch failed:', error.message);
      return null;
    }
  }

  // Get SOL/USD price (replaces xpService.getSOLPrice)
  async getSOLPrice() {
    try {
      if (this.solPriceCache && Date.now() - this.solPriceCacheTime < 60000) {
        return this.solPriceCache;
      }

      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
      if (response.status === 429) return this.solPriceCache || 200;
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      this.solPriceCache = data.solana?.usd || 200;
      this.solPriceCacheTime = Date.now();
      return this.solPriceCache;
    } catch {
      return this.solPriceCache || 200;
    }
  }

  // ===========================================================================
  // PUMP VERIFICATION & XP (existing)
  // ===========================================================================

  // Verify if a pump actually happened
  async verifyPump(tokenAddress, tokenSymbol, entryPrice, exitPrice, entryTime, exitTime) {
    try {
      // Get historical price data around the claimed times
      const priceAtEntry = await this.getHistoricalPrice(tokenAddress, entryTime);
      const priceAtExit = await this.getHistoricalPrice(tokenAddress, exitTime);
      
      // Tolerance for price verification (5% margin)
      const tolerance = 0.05;
      
      const entryValid = Math.abs(priceAtEntry - entryPrice) <= entryPrice * tolerance;
      const exitValid = Math.abs(priceAtExit - exitPrice) <= exitPrice * tolerance;
      
      return {
        verified: entryValid && exitValid,
        entryPriceActual: priceAtEntry,
        exitPriceActual: priceAtExit,
        entryPriceClaimed: entryPrice,
        exitPriceClaimed: exitPrice,
        accuracy: {
          entry: entryValid,
          exit: exitValid
        }
      };
    } catch (error) {
      logger.error('Error verifying pump:', error);
      return { verified: false, error: error.message };
    }
  }

  // Get historical price (simplified - would need more sophisticated implementation)
  async getHistoricalPrice(tokenAddress, timestamp) {
    // This is a simplified version - in production you'd need historical data APIs
    const currentPrice = await this.getTokenPrice(tokenAddress);
    return currentPrice?.price || 0;
  }

  // Calculate XP based on pump performance
  calculatePumpXP(pumpData) {
    const {
      entryPrice,
      exitPrice,
      volume24h,
      priceChange24h,
      detectionTime,
      trendingTime,
      isVerified
    } = pumpData;

    // Base multiplier from price change
    const priceMultiplier = exitPrice / entryPrice;
    
    // Volume weight (higher volume = more significant)
    const volumeWeight = Math.min(3, Math.max(0.5, Math.log10(volume24h / 1000)));
    
    // Accuracy factor (verified trades get bonus)
    const accuracyFactor = isVerified ? 1.5 : 1.0;
    
    // Early detection bonus
    let earlyBonus = 0;
    if (trendingTime && detectionTime < trendingTime) {
      const hoursEarly = (trendingTime - detectionTime) / (1000 * 60 * 60);
      earlyBonus = Math.max(0, hoursEarly) * 2.5;
    }
    
    // Calculate final XP
    const XP_base = Math.log(priceMultiplier) * volumeWeight * accuracyFactor;
    const XP_final = XP_base * (1 + earlyBonus);
    
    return {
      baseXP: Math.round(XP_base),
      earlyBonus: Math.round(earlyBonus),
      finalXP: Math.max(1, Math.round(XP_final)),
      factors: {
        priceMultiplier,
        volumeWeight,
        accuracyFactor,
        earlyBonus
      }
    };
  }

  // Get trending tokens (for pump detection)
  async getTrendingTokens(limit = 50) {
    try {
      const response = await fetch(`${this.baseUrls.dexscreener}/tokens/trending`);
      const data = await response.json();
      
      return data.tokens || [];
    } catch (error) {
      logger.error('Error fetching trending tokens:', error);
      return [];
    }
  }

  // Monitor token for pump detection
  async monitorToken(tokenAddress, callback) {
    const monitor = setInterval(async () => {
      const priceData = await this.getTokenPrice(tokenAddress);
      if (priceData) {
        callback(priceData);
      }
    }, 10000); // Check every 10 seconds

    return monitor; // Return interval ID for cleanup
  }

  // Stop monitoring
  stopMonitoring(monitorId) {
    if (monitorId) {
      clearInterval(monitorId);
    }
  }
}

// Export singleton instance
const priceService = new PriceService();
export default priceService;